import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";
import { ReporterSocialsProps } from "@/types/modules/post/widgets";
import styles from "@/styles/app.module.css";
import autoAnimate from "@formkit/auto-animate";
import { useSocialShares } from "@/hooks/useSocialShares";
import { pushToDataLayer } from "@/utils/pushToDataLayer";
import useScrollingRect from "@/hooks/useScrollingRect";
import PostPaywallUnlockAttemptModal from "@/components/modules/Post/Paywall/PostPaywallUnlockAttemptModal";

export const ReporterSocials: React.FC<ReporterSocialsProps> = ({ reporterSupportDescription, post }) => {
  const [tooltipText, setTooltipText] = useState("");

  const [isDialogVisible, setIsDialogVisible] = useState(false);

  const { shareOnTwitter, shareOnFacebook } = useSocialShares();

  const isCurrentSection = useScrollingRect("reporterSocials", 52);

  const copyToClipboard = () => {
    setTooltipText("Odkaz zkopírován");
    navigator.clipboard.writeText(post!.link);

    setTimeout(() => {
      setTooltipText("");
    }, 10000);
  };

  const parent = useRef(null);

  useEffect(() => {
    parent.current && autoAnimate(parent.current);
  }, [parent]);

  const handleUnlockButton = () => {
    // article_interaction (unlock)
    pushToDataLayer({
      event: "article_interaction",
      interaction_type: "unlock",
      interaction_option: post?.title ?? "article",
    });
    setIsDialogVisible(!isDialogVisible);
  };

  useEffect(() => {
    if (isCurrentSection) {
      pushToDataLayer({
        event: "article_interaction",
        interaction_type: "content_consumed",
      });
    }
  }, [isCurrentSection]);

  return (
    <div className={styles.contentContainer}>
      <div className="rounded-sm bg-gray-100 p-5 dark:bg-white-25 md:p-16" id="reporterSocials">
        <div className="flex items-center justify-between">
          <button
            onClick={() => shareOnTwitter(post!.local_link)}
            className="group relative mx-1 flex w-40 items-center justify-center rounded-full bg-dark py-3 transition-colors ease-in-out hover:bg-orange dark:bg-white dark:hover:bg-light-turquoise"
          >
            <Image
              src="/assets/images/icons/twitter-white.svg"
              alt="Twitter"
              width={20}
              height={20}
              className="dark:invert"
            />
          </button>

          <button
            onClick={() => shareOnFacebook(post!.local_link)}
            className="group relative mx-1 flex w-40 items-center justify-center rounded-full bg-dark py-3 transition-colors ease-in-out hover:bg-orange dark:bg-white dark:hover:bg-light-turquoise"
          >
            <Image
              src="/assets/images/icons/facebook-white.svg"
              alt="Facebook"
              width={20}
              height={20}
              className="dark:invert"
            />
          </button>

          <button
            ref={parent}
            onMouseEnter={() => setTooltipText("Zkopírovat odkaz")}
            onMouseLeave={() => setTooltipText("")}
            onClick={copyToClipboard}
            className="group relative mx-1 flex w-40 items-center justify-center rounded-full bg-dark py-3 transition-colors ease-in-out hover:bg-orange dark:bg-white dark:hover:bg-light-turquoise"
          >
            {tooltipText && (
              <span
                className={`${
                  tooltipText ? "block" : "hidden"
                } absolute -top-[58px] z-10 rounded-full bg-white px-[16px] py-[10px] font-sans text-sm shadow-lg md:group-hover:block`}
              >
                <span className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-x-8 border-t-[8px] border-white border-x-transparent" />
                <span className="flex items-center justify-center gap-[8px] whitespace-nowrap">
                  {tooltipText === "Odkaz zkopírován" && (
                    <Image src={"/assets/images/icons/success-green.svg"} alt={tooltipText} width={16} height={16} />
                  )}
                  {tooltipText}
                </span>
              </span>
            )}
            <Image
              src="/assets/images/icons/copy-link-white.svg"
              alt="Copy to clipboard"
              width={20}
              height={20}
              className="dark:invert"
            />
          </button>

          <button
            onClick={() => handleUnlockButton()}
            className="group relative mx-1 flex w-40 items-center justify-center rounded-full bg-dark py-3 transition-colors ease-in-out hover:bg-orange dark:bg-white dark:hover:bg-light-turquoise"
          >
            <Image
              src="/assets/images/icons/unlock-white.svg"
              alt="Unlock article"
              width={20}
              height={20}
              className="dark:invert"
            />
          </button>
        </div>
        <p className="mt-6 text-center dark:text-white sm:text-lg">{reporterSupportDescription}</p>
      </div>
      <PostPaywallUnlockAttemptModal isDialogVisible={isDialogVisible} setIsDialogVisible={setIsDialogVisible} />
    </div>
  );
};
